import React, { useState } from 'react';
import { HiServer } from 'react-icons/hi';
import { resetRedisAccessTokens } from '../../api/systemApi';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { Input } from '../../components/ui/Input';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import Toast, { type ToastType } from '../../components/ui/Toast';

type SettingsTab = 'system';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('system');
  const [confirmationText, setConfirmationText] = useState('');
  const [password, setPassword] = useState('');
  const [showFinalConfirmModal, setShowFinalConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: ToastType;
  }>({
    isVisible: false,
    message: '',
    type: 'info',
  });

  const REQUIRED_TEXT = '초기화 동의';
  const isButtonEnabled = confirmationText === REQUIRED_TEXT;

  const settingsTabs = [
    {
      id: 'system' as SettingsTab,
      label: '시스템 설정',
      icon: HiServer,
    },
  ];

  const handleClearCache = () => {
    if (!isButtonEnabled) return;
    setPassword(''); // 모달 열 때 비밀번호 초기화
    setShowFinalConfirmModal(true);
  };

  const handleFinalConfirm = async () => {
    if (!password.trim()) {
      setToast({
        isVisible: true,
        message: '비밀번호를 입력해주세요.',
        type: 'error',
      });
      return;
    }

    setShowFinalConfirmModal(false);
    setIsLoading(true);

    try {
      const result = await resetRedisAccessTokens(password);
      setToast({
        isVisible: true,
        message: `Redis 초기화 완료. (초기화된 키: ${result.flushedKeysCount.toLocaleString()}개)`,
        type: 'success',
      });
      // 성공 시 입력 필드 초기화
      setConfirmationText('');
      setPassword('');
    } catch (error: unknown) {
      let errorMessage = '초기화 중 오류가 발생했습니다. 잠시 후 다시 시도하거나 시스템 관리자에게 문의하십시오.';

      if (error instanceof Error) {
        // 백엔드에서 반환한 에러 메시지 사용
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            data?: { error?: string; message?: string };
          };
        };

        // 상태 코드별 에러 메시지
        if (axiosError.response?.status === 401) {
          errorMessage =
            axiosError.response.data?.error || axiosError.response.data?.message || '비밀번호가 일치하지 않습니다.';
        } else if (axiosError.response?.status === 403) {
          errorMessage =
            axiosError.response.data?.error ||
            axiosError.response.data?.message ||
            'Redis 캐시 초기화 권한이 없습니다.';
        } else if (axiosError.response?.status === 500) {
          errorMessage =
            axiosError.response.data?.error || axiosError.response.data?.message || '서버 오류가 발생했습니다.';
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }

      setToast({
        isVisible: true,
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6">
      <h1 className="mb-6 text-2xl font-bold text-slate-900">설정</h1>

      <div className="flex gap-6">
        {/* 왼쪽 메뉴 */}
        <div className="w-64 flex-shrink-0">
          <nav className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
            {settingsTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors ${
                    isActive ? 'bg-blue-50 font-semibold text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                  } `}>
                  <Icon className="h-5 w-5" />
                  <span className="text-sm">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* 오른쪽 컨텐츠 */}
        <div className="min-w-0 flex-1">
          {activeTab === 'system' && (
            <div className="space-y-6">
              {/* 시스템 설정 섹션 */}
              <Card title="시스템 설정">
                <div className="space-y-6">
                  {/* 캐시 관리 하위 섹션 */}
                  <div>
                    <h3 className="mb-4 text-base font-semibold text-slate-900">캐시 관리</h3>

                    {/* Redis 초기화 */}
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <div className="space-y-4">
                        <div>
                          <h4 className="mb-2 text-sm font-semibold text-slate-900">Redis 초기화</h4>
                          <p className="mb-4 text-sm text-slate-600">Redis 캐시를 초기화합니다.</p>
                        </div>

                        {/* 경고 메시지 */}
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                          <p className="text-sm font-medium text-red-800">
                            🚨 [필수 안내] 현재 접속 중인 모든 사용자의 로그인 세션이 즉시 삭제되어 로그아웃 처리됩니다.
                            신중하게 진행해 주십시오.
                          </p>
                        </div>

                        {/* 확인 문구 입력 필드 */}
                        <div>
                          <Input
                            type="text"
                            label="확인 문구"
                            placeholder="초기화 동의"
                            value={confirmationText}
                            onChange={(e) => setConfirmationText(e.target.value)}
                            disabled={isLoading}
                          />
                          <p className="mt-2 text-xs text-slate-500">
                            위 경고를 확인하셨다면 "{REQUIRED_TEXT}"를 정확히 입력해주세요.
                          </p>
                        </div>

                        {/* 실행 버튼 */}
                        <div className="flex justify-end">
                          <Button
                            onClick={handleClearCache}
                            disabled={!isButtonEnabled || isLoading}
                            variant={isButtonEnabled ? 'danger' : 'outline'}>
                            {isLoading ? '처리 중...' : '캐시 초기화 실행'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* 최종 확인 모달 */}
      <ConfirmModal
        isOpen={showFinalConfirmModal}
        onClose={() => {
          setShowFinalConfirmModal(false);
          setPassword('');
        }}
        type="warning"
        title="⚠️ 최종 경고: 모든 사용자 로그아웃"
        message={
          <div className="space-y-4">
            <p>
              이 작업을 실행하면 현재 서비스에 접속 중인 모든 일반 사용자들이 강제 로그아웃됩니다. 초기화를
              진행하시겠습니까?
            </p>
            <div className="mt-4">
              <Input
                type="password"
                label="관리자 비밀번호 확인"
                placeholder="비밀번호를 입력하세요"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
              <p className="mt-2 text-xs text-slate-500">보안을 위해 관리자 비밀번호를 한 번 더 입력해주세요.</p>
            </div>
          </div>
        }
        buttons={[
          {
            label: '취소',
            onClick: () => {
              setShowFinalConfirmModal(false);
              setPassword('');
            },
            variant: 'outline',
          },
          {
            label: '초기화 진행',
            onClick: handleFinalConfirm,
            variant: 'danger',
            disabled: !password.trim() || isLoading,
          },
        ]}
        infoBox={{
          content: (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">이 작업은 되돌릴 수 없습니다. 모든 사용자가 즉시 로그아웃됩니다.</p>
            </div>
          ),
        }}
      />

      {/* Toast 메시지 */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />

      {/* 로딩 오버레이 */}
      {isLoading && (
        <div className="bg-opacity-20 fixed inset-0 z-50 flex items-center justify-center bg-black">
          <div className="rounded-lg bg-white p-6 shadow-lg">
            <LoadingSpinner message="Redis 캐시 초기화 중..." />
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
